import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';

/** The part of a catalog tag a chip needs. `TagDef` satisfies it; a tag the catalog does not know is `{ key, label: key, muted }`. */
export interface TagChipTag {
  key: string;
  label: string;
  /** Hex colour from the catalog. Anything that is not a plain hex colour is ignored (the chip falls back to the accent). */
  color?: string;
  /** False = the tag was retired in the catalog: the chip is drawn greyed. Absent = active. */
  isActive?: boolean;
  /** The catalog does not know this key (a deleted tag, or the catalog has not loaded): drawn greyed, with the raw key. */
  unknown?: boolean;
}

interface TagChipProps {
  tag: TagChipTag;
  /** When given, the chip shows a remove button (the tag editor); the list rows never do. */
  onRemove?: () => void;
  small?: boolean;
}

/** `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa` only: the colour reaches a CSS custom property, so nothing else is accepted. */
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * One tag as a small coloured chip (ADR-033 D8: the console shows and attaches EXISTING catalog tags; it never creates
 * one). The chip never shows a staff id and never renders the colour as anything but a validated hex custom property.
 */
export const TagChip: React.FC<TagChipProps> = ({ tag, onRemove, small = false }) => {
  const { t } = useT();
  const muted = tag.isActive === false || tag.unknown === true;
  const color = tag.color && HEX_COLOR.test(tag.color.trim()) ? tag.color.trim() : undefined;
  return (
    <span
      className={'tag-chip' + (small ? ' sm' : '') + (muted ? ' muted' : '')}
      style={color ? ({ ['--tag' as string]: color } as React.CSSProperties) : undefined}
      data-testid={`tag-chip-${tag.key}`}
      data-muted={muted ? 'true' : 'false'}
    >
      <span className="tag-dot" aria-hidden="true" />
      <span dir="auto">{tag.label}</span>
      {onRemove && (
        <button
          type="button"
          className="tag-x"
          onClick={onRemove}
          aria-label={t('tags_remove', { label: tag.label })}
          data-testid={`tag-remove-${tag.key}`}
        >
          <Icon name="x" size={11} />
        </button>
      )}
    </span>
  );
};
